import router from 'next/router';
import { useCallback, useContext, useMemo, useState } from 'react';
import styles from './AssistantPopover.module.scss';
import { CommonImage } from '~/components/common/image/CommonImage';
import { BasicParagraph } from '~/components/common/text/BasicParagraph';
import { BasicSpan } from '~/components/common/text/BasicSpan';
import { ModalContext } from '~/context/ModalContext';
import type { AssistantEventId } from '~/features/assistant/hooks/useAssistant';
import { useAssistantInterval } from '~/features/assistant/hooks/useAssistantInterval';
import { RecommendModal } from '~/features/recommend/components/RecommendModal';
import { useGetRecommendedProduct } from '~/features/recommend/hooks/useGetRecommendedProduct';
import type { Product } from '~/features/recommend/interfaces/models';
import { productAdapter } from '~/features/recommend/productAdapter';
import { useForecastGraph } from '~/features/record/hooks/useForecastGraph';
import { useGrowthPhase } from '~/features/record/hooks/useGrowthPhase';
import { useTranslation } from '~/hooks/useTranslation';
import { isLocalizeKey } from '~/interfaces/i18n';
import { assistantPersistences } from '~/persistence';
import { path } from '~/utils/path';
import { currentDateTime } from '~/utils/time';

export interface PopoverEvent {
  eventId: AssistantEventId;
  destination?: string;
  message: string;
  need_recommended: boolean;
}

interface Props {
  assistantEvents: PopoverEvent[];
}

export const AssistantPopover = ({ assistantEvents }: Props) => {
  const { t } = useTranslation();
  const { data: growth } = useForecastGraph();
  const { data: stage } = useGrowthPhase();
  const { setAssistantInterval } = useAssistantInterval();
  const [closedEventIds, setClosedEventIds] = useState<AssistantEventId[]>([]);
  const { openModal } = useContext(ModalContext);
  const today = currentDateTime();
  const { data: recommendedData } = useGetRecommendedProduct({
    date: today.toFormat('yyyy-MM-dd'),
  });

  const event = useMemo(
    () => assistantEvents.filter((event) => !closedEventIds.includes(event.eventId))[0],
    [closedEventIds, assistantEvents],
  );

  const products: Product[] = useMemo(() => {
    if (!recommendedData) return [];
    return recommendedData.products.map((product) => productAdapter(product));
  }, [recommendedData]);

  const closeAssistantPopover = useCallback(() => {
    setClosedEventIds([...closedEventIds, event.eventId]);
    setAssistantInterval(event.eventId);
    if (event.eventId === 'client_visit_home_from_measure') {
      assistantPersistences.ShowFromMeasure.set(false);
    }
    if (event.eventId === 'client_visit_home_from_cloakroom') {
      assistantPersistences.ShowFromCloakroom.set(false);
    }
  }, [closedEventIds, event, setAssistantInterval]);

  const onClickButton = useCallback(async () => {
    if (!event.destination) return;
    setAssistantInterval(event.eventId);
    if (event.eventId === 'grown_up_and_retention') {
      const today = currentDateTime();
      openModal(() =>
        RecommendModal({
          products: products,
          page: 'home',
          date: today,
          selectedProductIndex: 1,
          title: t('predict_chart_grown').toString(),
          children: null,
        }),
      );
      setClosedEventIds([...closedEventIds, event.eventId]);
    } else {
      await router.push(event.destination);
    }
  }, [event, setAssistantInterval, closedEventIds, products, openModal, t]);

  const translateMessage = useMemo(() => {
    if (!event) return '';
    const { eventId, message } = event;
    if (!isLocalizeKey(message)) return message;
    if (eventId === 'client_visit_home_from_measure') {
      if (!growth) return;
      const graphData = growth.graph_data;
      const data = graphData.filter((data) => data.left_length && data.right_length);
      const before = data[data.length - 2];
      const after = data[data.length - 1];
      return t(message, {
        before: Math.max(before.left_length ?? 0, before.right_length ?? 0),
        after: Math.max(after.right_length ?? 0, after.right_length ?? 0),
      });
    } else if (eventId === 'client_visit_record_growth') {
      return t(message, { stage: stage?.title ?? '' });
    }
    return t(message);
  }, [event, stage, t, growth]);

  if (!event) return <></>;

  return (
    <div className={styles['container']}>
      <button
        className={styles['content']}
        data-has-link={!!event?.destination}
        onClick={closeAssistantPopover}
      >
        <picture className={styles['asics-icon']}>
          <CommonImage src={path.image('/assistant/asics-icon.svg')} width={100} height={100} />
        </picture>
        <BasicSpan
          size={20}
          fontFamily="sen"
          colorStyle="addBlue"
          letterSpacing="0.03em"
          lineHeight={1}
          weight={700}
        >
          ASICS ASSISTANT
        </BasicSpan>
        <BasicParagraph size={24} lineHeight={1.67} weight={700} className={styles['message']}>
          {translateMessage}
        </BasicParagraph>
      </button>
      {event?.destination && (
        <button className={styles['link']} onClick={onClickButton}>
          <CommonImage src={path.image('/assistant/link.svg')} width={11} height={17} />
        </button>
      )}
    </div>
  );
};
